import { useEffect, type FC, useState, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Edit, KeyboardArrowDown } from '@mui/icons-material';
import { Scrollbar } from 'src/components/scrollbar';
import { useDispatch, useSelector } from 'react-redux';
import { getAllFacilitiesStays } from 'src/redux/actions/facilityActions';
import { useNavigate } from 'react-router';
import { paths } from 'src/paths';
import Plus from '@untitled-ui/icons-react/build/esm/Plus';
import { Button } from '@mui/material';
import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown';
import ChevronUpIcon from '@untitled-ui/icons-react/build/esm/ChevronUp';
import { convertDateToSelectedTimeZone } from 'src/utils/convertDateToSelectedTimeZone';
import moment from 'moment';

interface FacilityStayTableProps {
  patientId: string;
  patientData: any;
}

interface FacilityStayData {
  uuid: string;
  facilityName: string;
  placeOfResidence: string;
  startDate: string;
  dischargeDate: string;
}

export const FacilityStayTable: FC<FacilityStayTableProps> = ({ patientId, patientData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getFacilitiesStay = useSelector((state: any) => state.getAllFacilitiesStaysReducer);
  const [getFacilityStays, setFacilitiesStay] = useState([]);
  const [noRecordsFound, setNoRecordsFound] = useState<any>(null);
  const [tableSlice, setTableSlice] = useState(true);
  const [facilitiesCounter, setFacilityCounter] = useState(0);
  const { patients, patientDetails, editFacilityStay, facilityStay, addFacilityStay } = paths;

  const [displayInitialRecords, setDisplayInitialRecords] = useState(3);
  const [displayAllRecords, setDisplayAllRecords] = useState(false);
  const lastRowRef = useRef(null);

  useEffect(() => {
    if (!patientId) return;
    dispatch(getAllFacilitiesStays(patientId, {}));
  }, [patientId]);

  useEffect(() => {
    if (!getFacilitiesStay.isGetFacilitiesStay) return;
    if (getFacilitiesStay?.response?.data?.data?.content?.length === 0) {
      setNoRecordsFound(true);
    } else {
      setFacilitiesStay(getFacilitiesStay?.response?.data?.data?.content);
      setFacilityCounter(getFacilitiesStay?.response?.data?.data?.totalElements);
    }
  }, [getFacilitiesStay]);

  const handleNavigation = (facilityStay: FacilityStayData) => {
    navigate(`${patients}/${patientDetails}${editFacilityStay}`, {
      state: { data: facilityStay, action: 'Edit' },
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setDisplayInitialRecords((prevRecords) => prevRecords + 20);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    if (lastRowRef.current && !displayAllRecords) {
      observer.observe(lastRowRef.current);
    }

    return () => {
      if (lastRowRef.current && !displayAllRecords) {
        observer.unobserve(lastRowRef.current);
      }
    };
  }, [lastRowRef, displayAllRecords]);

  return (
    <Box
      sx={{
        marginTop: '32px',
      }}
    >
      <Card>
        <CardHeader
          title={
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Box>
                <Typography
                  variant="h6"
                  display={'inline-block'}
                >
                  Facility Stays
                </Typography>
                <Box
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.26)',
                    display: 'inline-block',
                    paddingTop: '3px',
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    paddingBottom: '3px',
                  }}
                  ml={2}
                >
                  <Typography variant="subtitle2">{`${getFacilitiesStay?.response?.data?.data?.totalElements} Facilities`}</Typography>
                </Box>
              </Box>
              <Box>
                <Button
                  color="inherit"
                  startIcon={
                    <SvgIcon>
                      <Plus />
                    </SvgIcon>
                  }
                  onClick={(event) => navigate(`${patients}/${facilityStay}/${addFacilityStay}`)}
                  sx={{ color: 'primary.dark' }}
                >
                  Add New Facility Stay
                </Button>
              </Box>
            </Box>
          }
        />
        <Divider />
        <Scrollbar>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Facility Name</TableCell>
                <TableCell>Residence</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFacilitiesStay?.response?.data?.data?.content?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Typography
                        color={'text.secondary'}
                        variant="subtitle2"
                      >
                        No Records Found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                getFacilitiesStay?.response?.data?.data?.content
                  .slice(0, tableSlice ? 3 : undefined)
                  .map((facilityStay: FacilityStayData, index: any) => {
                    const startDateFormat: any = facilityStay?.startDate?.split('T')?.at(0);
                    const startDateWithoutTimezone = new Date(
                      new Date(startDateFormat).valueOf() +
                        new Date(startDateFormat).getTimezoneOffset() * 60 * 1000
                    );

                    const endDateFormat: any = facilityStay?.dischargeDate?.split('T')?.at(0);
                    const endDateWithoutTimezone = new Date(
                      new Date(endDateFormat).valueOf() +
                        new Date(endDateFormat).getTimezoneOffset() * 60 * 1000
                    );

                    return (
                      <TableRow
                        hover
                        key={facilityStay?.uuid}
                        ref={
                          !displayAllRecords && index === displayInitialRecords - 1
                            ? lastRowRef
                            : null
                        }
                      >
                        <TableCell>
                          <Typography variant="subtitle2">
                            {facilityStay?.facilityName ? facilityStay.facilityName : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {facilityStay?.placeOfResidence
                            ? facilityStay?.placeOfResidence
                                .replace(/_/g, ' ')
                                .replace(/\w\S*/g, function (txt) {
                                  return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                                })
                            : '-'}
                        </TableCell>

                        <TableCell>
                          {facilityStay?.startDate
                            ? moment(startDateWithoutTimezone).format('MMM DD, yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {facilityStay?.dischargeDate
                            ? moment(endDateWithoutTimezone).format('MMM DD, yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleNavigation(facilityStay)}>
                            <SvgIcon>
                              <Edit />
                            </SvgIcon>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </Scrollbar>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          sx={{
            p: 2,
          }}
        >
          <Button
            color="primary"
            size="small"
            onClick={() => setTableSlice(!tableSlice)}
          >
            {tableSlice ? 'See All Facility Stays' : 'Hide Facility Stays'}
          </Button>
          <Button
            color="primary"
            endIcon={
              <SvgIcon onClick={() => setTableSlice(!tableSlice)}>
                {tableSlice ? <ChevronDownIcon /> : <ChevronUpIcon />}
              </SvgIcon>
            }
          ></Button>
        </Box>
      </Card>
    </Box>
  );
};
